// @ts-check
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import { OrderImportSection } from './order-import-section';
import style from './style.module.less';
import { useOrderCSVImportContext } from '../../../../../../components/order-import/order-import-context';
import { useEnableNewOrderImport } from '../../../../../../feature-flags/hooks/use-enable-new-order-import';
import { FTU_WIZARD_PATH } from '../../../../../../routes/constants';
import Global from '../../../../../../store/global-selectors';
import { EventSourceStrings, Workflows } from '../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import MarketplaceSetupModal from '../../../../../settings/stores/marketplace-connection-modal/setup';
import V2MarketplaceSetupModal from '../../../../../settings/stores/v2-modals/marketplace-setup';
import { useMarketplaceContext } from '../../context/marketplace-context';

const DUMMY_MARKETPLACE_ID = 1000;
const restrictedMarketplaceIds = [DUMMY_MARKETPLACE_ID];

/**
 * @param {object} props
 * @param {(hasCsvFile: boolean) => void} props.onCsvFileChange
 * @param {(importReady: {initImport: (onSuccess: () => void) => void, onImport: (onSuccess: () => void) => void, hasFile?: boolean, hasStore?: boolean} | null) => void} [props.onImportReadyChange]
 * @param {(hasManualOrder: boolean) => void} [props.onManualOrderAdded]
 * @returns {JSX.Element}
 */
export const OrdersStep = ({ onCsvFileChange, onImportReadyChange, onManualOrderAdded }) => {
  const allSellerActiveStores = useSelector(Global.selectSellerStores).filter(
    /**
     * @param {*} store
     * @returns {boolean}
     */
    (store) => store.isActive && !store.isManualStore && !restrictedMarketplaceIds.includes(store.marketplaceId),
  );

  const { setupMarketplace, closeSetupModal, onMarketplaceSetupBack, onSaveNewMarketplace, onError, onRedirectToMarketplaceSetup } =
    useMarketplaceContext();

  const orderCSVContext = useOrderCSVImportContext();
  const openCSVImport = /** @type {*} */ (orderCSVContext)?.openCSVImport;
  const isNewOrderImportEnabled = useEnableNewOrderImport();
  const workflow = isNewOrderImportEnabled ? Workflows.CSV_ORDER_IMPORT_FLOW : Workflows.ORDER_IMPORT_FLOW;
  const trackOrderImport = useTrackWorkflow(workflow);

  const handleImportEvent = useCallback(() => {
    trackOrderImport(WorkflowSteps.START, {
      source: EventSourceStrings.FTU_WIZARD_IMPORT_CSV,
    });
    if (openCSVImport) {
      openCSVImport();
    }
  }, [openCSVImport, trackOrderImport]);

  return (
    <>
      <div className={style['orders-step']}>
        <Text className={style.header} textType={TextType.H2} value={messages.ftuOrderStepTitle} />
        <Text
          formattedMessageValues={{
            a: (/** @type {*} */ chunks) => (
              <Button onClick={handleImportEvent} theme='link'>
                {chunks}
              </Button>
            ),
          }}
          textType={TextType.Description}
          value={messages.ftuOrderStepDescription}
        />
      </div>
      <OrderImportSection
        activeStores={allSellerActiveStores}
        onCsvFileChange={onCsvFileChange}
        onImportReadyChange={onImportReadyChange}
        onManualOrderAdded={onManualOrderAdded}
      />
      {setupMarketplace && !setupMarketplace.isSupported ? (
        <V2MarketplaceSetupModal marketplaceId={setupMarketplace.marketplaceId} onClose={closeSetupModal} onSave={onSaveNewMarketplace} />
      ) : null}
      {setupMarketplace && setupMarketplace.isSupported ? (
        <MarketplaceSetupModal
          marketplaceApiCode={setupMarketplace.apiCode}
          onBack={onMarketplaceSetupBack}
          onClose={closeSetupModal}
          onError={onError}
          onRedirect={onRedirectToMarketplaceSetup}
          onSave={onSaveNewMarketplace}
          redirectPath={FTU_WIZARD_PATH}
          storeGuid={undefined}
        />
      ) : null}
    </>
  );
};
